import { z } from 'zod';
import { DeliveryOptions, PUSH_PLATFORMS } from './delivery-options.ts';
import { PUSH_SYNC_HINTS, PushAction, PushButton, PushExtraValue } from './sdk-payload.ts';

/**
 * A push personalized for one platform: the output of `PushRenderer` (BB-73) and the input of the FCM / APNs wire
 * builders. Server-internal (never sent as is): the builders turn it into the `bb` payload plus FCM `android` options
 * or APNs `aps` and headers. Platform-specific fields are refused on the other platform so a renderer bug shows up
 * here and not as a silently dropped field.
 */
const HttpsUrl = z.url({ protocol: /^https$/, error: 'must be an https:// URL' }).max(2048);
const Text = z.string().min(1).max(4096);
const Id64 = z.string().min(1).max(64);

const ANDROID_ONLY = ['summary', 'androidChannelId', 'largeIconUrl'] as const;
const IOS_ONLY = ['subtitle', 'iosCategoryId', 'iosThreadId', 'iosInterruptionLevel', 'iosRelevanceScore', 'badge'] as const;
const VISIBLE_ONLY = ['title', 'subtitle', 'body', 'summary', 'imageUrl', 'largeIconUrl', 'iosInterruptionLevel', 'iosRelevanceScore'] as const;

export const RenderedPush = z
  .strictObject({
    platform: z.enum(PUSH_PLATFORMS),
    kind: z.enum(['visible', 'silent']),
    title: Text.optional(),
    subtitle: Text.optional(),
    body: Text.optional(),
    summary: Text.optional(),
    androidChannelId: Id64.optional(),
    iosCategoryId: Id64.optional(),
    iosThreadId: Id64.optional(),
    iosInterruptionLevel: z.enum(['passive', 'active', 'time-sensitive']).optional(),
    iosRelevanceScore: z.number().min(0).max(1).optional(),
    /** iOS app icon badge: set a number or clear it (BB-82). */
    badge: z.union([z.strictObject({ set: z.int().min(0) }), z.strictObject({ clear: z.literal(true) })]).optional(),
    /** `default` plays the system sound; null or absent — silent notification. */
    sound: z.literal('default').nullable().optional(),
    imageUrl: HttpsUrl.optional(),
    largeIconUrl: HttpsUrl.optional(),
    action: PushAction,
    buttons: z.array(PushButton).max(3),
    extras: z.record(z.string().min(1).max(100), PushExtraValue),
    sync: z.array(z.enum(PUSH_SYNC_HINTS)).min(1).max(PUSH_SYNC_HINTS.length).optional(),
    options: DeliveryOptions,
  })
  .superRefine((push, ctx) => {
    const refuse = (fields: readonly (keyof typeof push)[], message: string) => {
      for (const field of fields) if (push[field] !== undefined) ctx.addIssue({ code: 'custom', path: [field], message });
    };
    if (push.platform === 'android') refuse(IOS_ONLY, 'iOS only');
    else refuse(ANDROID_ONLY, 'Android only');
    if (push.platform === 'ios' && push.options.androidNotificationId !== undefined) ctx.addIssue({ code: 'custom', path: ['options', 'androidNotificationId'], message: 'Android only' });
    if (push.kind === 'silent') {
      refuse(VISIBLE_ONLY, 'a silent push shows nothing');
      if (push.buttons.length > 0) ctx.addIssue({ code: 'custom', path: ['buttons'], message: 'a silent push shows nothing' });
      if (push.sound) ctx.addIssue({ code: 'custom', path: ['sound'], message: 'a silent push shows nothing' });
    } else if (push.title === undefined && push.body === undefined) {
      ctx.addIssue({ code: 'custom', path: ['body'], message: 'a visible push needs a title or a body' });
    }
    if (new Set(push.buttons.map((b) => b.id)).size !== push.buttons.length) ctx.addIssue({ code: 'custom', path: ['buttons'], message: 'button ids must be unique' });
  });
export type RenderedPush = z.infer<typeof RenderedPush>;
